import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { getApiBase, fetchJson } from '../../apiBase';
import { formatIncomingMessagePreview } from './incomingMessagePreview';

function api() {
  const base = `${getApiBase()}/admin`;
  return {
    incoming: (school, limit = 50, offset = 0) =>
      `${base}/messages/incoming/${encodeURIComponent(school)}?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`,
  };
}

function formatTimestamp(ts) {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('tr-TR', { hour12: false, timeZone: 'Europe/Istanbul' });
  } catch {
    return ts;
  }
}

export { formatIncomingMessagePreview } from './incomingMessagePreview';

export default function IncomingMessagesLogPage({ schoolId, onError }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const apis = useMemo(() => api(), []);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await fetchJson(apis.incoming(schoolId, 50, 0));
      if (!res.ok) throw new Error('failed');
      const payload = await res.json();
      setItems(Array.isArray(payload?.items) ? payload.items : []);
      setTotal(Number(payload?.total || 0));
    } catch (e) {
      onError && onError('Gelen mesajlar yuklenemedi');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apis, onError, schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ minHeight: 360 }}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Son 50 Gelen Veli Mesaji
        </Typography>
        <Chip size="small" color="primary" label={`Toplam: ${total}`} />
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small" aria-label="gelen mesajlar tablosu">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Ad Soyad</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Telefon</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sınıf</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tarih</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Mesaj</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>Gosterilecek gelen mesaj bulunamadi.</TableCell>
              </TableRow>
            )}
            {items.map((msg) => (
              <TableRow key={msg.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{msg.parent_name || 'Veli'}</TableCell>
                <TableCell>{msg.phone || '-'}</TableCell>
                <TableCell>{msg.related_class || '-'}</TableCell>
                <TableCell>{formatTimestamp(msg.timestamp)}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{formatIncomingMessagePreview(msg)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
